import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { queryClient } from '../../'
import i18n from '../../i18n'
import { authenticateDestroy, authenticateSuccess, resetPasswordSuccess } from '../../store/auth'
import { fetchCurrentAgent, forgotPassword, impersonate, invalidateToken, login, register, resetPassword, saveAgentLanguage } from './functions'
import { clearTokens, isImpersonated, setTokens } from './tokenStorage'
import {
    CurrentAgent,
    ForgotPasswordPayload,
    ImpersonateErrorResponse,
    ImpersonatePayload,
    LoginPayload,
    LoginResponse,
    RegisterPayload,
    ResetPasswordPayload,
    SaveAgentLanguageErrorResponse,
    SaveAgentLanguageResponse,
} from './types'

const auth = {
    Login: {
        useMutation: (options?: UseMutationOptions<LoginResponse, AxiosError, LoginPayload>) => {
            const dispatch = useDispatch()

            return useMutation({
                mutationFn: (payload: LoginPayload) => login(payload),
                onSuccess: (data) => {
                    setTokens(data.token, data.refresh_token)
                    dispatch(authenticateSuccess({ data }))
                    queryClient.clear()

                    if (data.permissionsChanged) {
                        toast.info(i18n.t('toast.permissionsChanged') as string, { autoClose: 8000 })
                    }
                },
                ...options,
            })
        },
    },
    Register: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, RegisterPayload>) => {
            return useMutation({
                mutationFn: (payload: RegisterPayload) => register(payload),
                ...options,
            })
        },
    },
    ForgotPassword: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, ForgotPasswordPayload>) => {
            return useMutation({
                mutationFn: (payload: ForgotPasswordPayload) => forgotPassword(payload),
                ...options,
            })
        },
    },
    ResetPassword: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, ResetPasswordPayload>) => {
            const dispatch = useDispatch()

            return useMutation({
                mutationFn: (payload: ResetPasswordPayload) => resetPassword(payload),
                onSuccess: () => {
                    // Login reads this cross-page (redux survives the redirect to /login,
                    // a mutation's local state wouldn't) to show the "password reset" banner
                    dispatch(resetPasswordSuccess())
                },
                ...options,
            })
        },
    },
    Logout: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, void>) => {
            const dispatch = useDispatch()

            return useMutation({
                mutationFn: invalidateToken,
                // onSettled (not onSuccess) — always clear locally and reset the store even if
                // the server-side invalidate call fails
                onSettled: () => {
                    clearTokens()
                    dispatch(authenticateDestroy())
                    queryClient.clear()
                },
                ...options,
            })
        },
    },
    Impersonate: {
        useMutation: (options?: UseMutationOptions<LoginResponse, AxiosError<ImpersonateErrorResponse>, ImpersonatePayload>) => {
            const dispatch = useDispatch()

            return useMutation({
                mutationFn: async (payload: ImpersonatePayload) => {
                    // Destroy the current session before exchanging for a new impersonated one
                    if (isImpersonated()) {
                        try {
                            await invalidateToken()
                        } finally {
                            clearTokens()
                            dispatch(authenticateDestroy())
                            queryClient.clear()
                        }
                    }

                    return impersonate(payload)
                },
                onSuccess: (data) => {
                    setTokens(data.token, data.refresh_token)
                    dispatch(authenticateSuccess({ data }))
                },
                ...options,
            })
        },
    },
    CurrentAgent: {
        useQuery: <TData = CurrentAgent>(options?: Omit<UseQueryOptions<CurrentAgent, AxiosError, TData>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: ['currentAgent'],
                queryFn: fetchCurrentAgent,
                // Refetch via explicit invalidateQueries(['currentAgent']) and the admin agent/agency/office mutations
                staleTime: Infinity,
                ...options,
            }),
    },
    SaveAgentLanguage: {
        useMutation: (options?: UseMutationOptions<SaveAgentLanguageResponse, AxiosError<SaveAgentLanguageErrorResponse>, string>) =>
            useMutation({
                mutationFn: (locale: string) => saveAgentLanguage(locale),
                onSuccess: () => window.location.reload(),
                ...options,
            }),
    },
}

export default auth
